import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const THEME_STORAGE_KEY = 'mezcal.explorer.theme';
const THEMES = ['explorer-light', 'explorer-dark'] as const;
const DEFAULT_THEME: ExplorerTheme = 'explorer-dark';

export type ExplorerTheme = (typeof THEMES)[number];

function isExplorerTheme(value: string): value is ExplorerTheme {
  return THEMES.includes(value as ExplorerTheme);
}

function resolveInitialTheme(): ExplorerTheme {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME;
  }

  try {
    const persistedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (persistedTheme && isExplorerTheme(persistedTheme)) {
      return persistedTheme;
    }
  } catch {
    return DEFAULT_THEME;
  }

  return DEFAULT_THEME;
}

type ThemeContextValue = {
  readonly theme: ExplorerTheme;
  readonly isDark: boolean;
  readonly setTheme: (value: ExplorerTheme) => void;
  readonly toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [theme, setTheme] = useState<ExplorerTheme>(resolveInitialTheme);
  const isDark = theme === 'explorer-dark';

  const toggleTheme = useCallback(() => {
    setTheme((currentTheme) =>
      currentTheme === 'explorer-dark' ? 'explorer-light' : 'explorer-dark',
    );
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage failures; theme still works for current session.
    }
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ isDark, setTheme, theme, toggleTheme }),
    [isDark, theme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('Theme context unavailable');
  }

  return context;
}
